import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { quizRegistry } from "@quizzes/_registry";
import type { Question, Quiz } from "@quizzes/_schema";
import {
  DateRangePicker,
  isoDaysAgo,
  isoToday,
  toRangeIso,
} from "../components/DateRangePicker";
import { ConversionHero } from "../components/ConversionHero";
import { DevicesCard } from "../components/DevicesCard";
import { ExperimentStatusBar } from "../components/ExperimentStatusBar";
import { PagesTable } from "../components/PagesTable";
import { TimeSeriesChart } from "../components/TimeSeriesChart";
import {
  type FunnelParams,
  type FunnelResponse,
  type ResponseBreakdown,
  api,
  ApiError,
} from "../api";
import { buildBreakdownCsv } from "../utils/csv";
import { isRandomizedQuestion } from "../utils/questions";

// Question types that have an option-style response breakdown worth fetching.
const BREAKDOWN_TYPES = new Set(["single", "multi", "slider"]);

// Shown when the funnel `fetch` REJECTS rather than returning a status. The
// browser cannot tell us why: API Gateway's 504 is emitted without CORS
// headers, so a request the backend spent its full 30s on is indistinguishable
// from an offline laptop -- both arrive as an opaque TypeError. The previous
// text ("Failed to load funnel") was accurate and useless; on 2026-08-18 it sat
// over an em-dashed stat row while the real cause, twelve Lambda invocations
// timing out at exactly 30,000ms, was only visible in CloudWatch. Name the
// likely cause and the lever the operator actually has. A real HTTP error
// still reports the server's own detail string -- see the ApiError branch.
const FUNNEL_UNREACHABLE =
  "The funnel request did not respond. This is usually a backend timeout on a " +
  "wide window — try a narrower date range, or check the API logs.";

// Max per-question breakdown requests in flight at once. Each breakdown is a
// live full-window GROUP-BY; running too many at once pegs the small DB's CPU
// and starves the top-line funnel into the 30s gateway timeout (2026-06-19
// "Failed to load funnel" was DB CPU at ~100%, not connection exhaustion).
// Kept low; results still fill in progressively.
const BREAKDOWN_CONCURRENCY = 2;

function questionLabel(q: Question): string {
  return q.type === "info" ? q.heading : q.prompt;
}

function questionLabelMap(quiz: Quiz): Record<string, string> {
  const out: Record<string, string> = {};
  for (const q of quiz.questions) out[q.id] = questionLabel(q);
  return out;
}

function formatDuration(ms: number | null): string {
  if (ms === null) return "—";
  const seconds = Math.round(ms / 1000);
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return value.toLocaleString("en-US");
}

export function Analytics() {
  const [searchParams, setSearchParams] = useSearchParams();
  const quizzes = useMemo(() => Object.values(quizRegistry), []);
  const [slug, setSlug] = useState<string>(
    searchParams.get("slug") ?? quizzes[0]?.slug ?? "",
  );
  const [from, setFrom] = useState<string>(isoDaysAgo(7));
  const [to, setTo] = useState<string>(isoToday());
  const [cohort, setCohort] = useState<"all" | "completers" | "abandoners">(
    "all",
  );
  const [persona, setPersona] = useState<string>("");
  const [denominator, setDenominator] = useState<"answered" | "started">(
    "answered",
  );
  const [data, setData] = useState<FunnelResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Per-question response breakdowns, fetched lazily (one request per question,
  // in parallel) so the top-line funnel loads fast and no single request
  // computes every breakdown over the full window — that inline pass timed out.
  const [breakdowns, setBreakdowns] = useState<
    Record<string, ResponseBreakdown[]>
  >({});
  const [breakdownsLoading, setBreakdownsLoading] = useState(false);

  const quiz = quizRegistry[slug];

  const questionTypes = useMemo(() => {
    if (!quiz) return {};
    return Object.fromEntries(quiz.questions.map((q) => [q.id, q.type]));
  }, [quiz]);

  const optionLabels: Record<string, Record<string, string>> = useMemo(() => {
    const out: Record<string, Record<string, string>> = {};
    if (!quiz) return out;
    for (const q of quiz.questions) {
      const labels: Record<string, string> = {};
      if (q.type === "single" || q.type === "multi") {
        for (const o of q.options) labels[o.value] = o.label;
      }
      out[q.id] = labels;
    }
    return out;
  }, [quiz]);

  const randomizedQuestionIds = useMemo(() => {
    const ids = new Set<string>();
    if (!quiz) return ids;
    for (const q of quiz.questions) {
      if (isRandomizedQuestion(q)) ids.add(q.id);
    }
    return ids;
  }, [quiz]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set("slug", slug);
    setSearchParams(next, { replace: true });
  }, [slug, searchParams, setSearchParams]);

  useEffect(() => {
    if (!quiz) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const range = toRangeIso(from, to);
    const params: FunnelParams = {
      slug: quiz.slug,
      from: range.from,
      to: range.to,
      questionIds: quiz.questions.map((q) => q.id),
      questionTypes,
      cohort,
    };
    if (persona) params.persona = persona;
    api
      .funnel(params)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError) setError(err.message || `HTTP ${err.status}`);
        else setError(FUNNEL_UNREACHABLE);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [quiz, from, to, questionTypes, cohort, persona]);

  // Lazily fetch each question's response breakdown AFTER the top-line funnel
  // resolves, with bounded concurrency. Firing the breakdowns alongside the
  // funnel made one page load ~13 concurrent requests, spinning up enough
  // parallel Lambdas to exhaust the DB connection pool -- and the top-line lost
  // the race against its own breakdown siblings ("Failed to load funnel").
  // Gating on `data` (and `!loading`) keeps the top-line from competing for a
  // connection; BREAKDOWN_CONCURRENCY caps the fan-out. Results fill in
  // progressively. Each request is still bounded to one question so none can
  // exceed the request timeout the old all-questions-inline pass did.
  useEffect(() => {
    if (!quiz || loading || !data) return;
    let cancelled = false;
    setBreakdowns({});
    const range = toRangeIso(from, to);
    const targets = quiz.questions.filter((q) => BREAKDOWN_TYPES.has(q.type));
    if (targets.length === 0) {
      setBreakdownsLoading(false);
      return;
    }
    setBreakdownsLoading(true);

    const slug = quiz.slug;
    let cursor = 0;
    async function fetchOne(q: Question): Promise<void> {
      try {
        const res = await api.breakdown({
          slug,
          from: range.from,
          to: range.to,
          questionId: q.id,
          questionType: q.type,
          cohort,
          persona: persona || undefined,
        });
        if (!cancelled)
          setBreakdowns((prev) => ({ ...prev, [q.id]: res.responses }));
      } catch {
        // Leave this question's breakdown empty; the row still renders.
      }
    }
    async function worker(): Promise<void> {
      while (!cancelled) {
        const idx = cursor++;
        if (idx >= targets.length) return;
        const q = targets[idx];
        if (!q) return;
        await fetchOne(q);
      }
    }
    const pool = Array.from(
      { length: Math.min(BREAKDOWN_CONCURRENCY, targets.length) },
      () => worker(),
    );
    void Promise.all(pool).finally(() => {
      if (!cancelled) setBreakdownsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [quiz, data, loading, from, to, cohort, persona]);

  if (!quiz) {
    return <div className="text-mars-white/60">No quizzes registered.</div>;
  }

  const labels = questionLabelMap(quiz);

  return (
    <div>
      <header className="mb-8 flex flex-wrap items-end justify-between gap-6">
        <div>
          <span className="eyebrow">Funnel</span>
          <h1 className="heading mt-1 text-3xl">{quiz.meta.title}</h1>
          <p className="mt-2 text-sm text-mars-white/60">
            Visitors, submits, conversion rate and per-page drop-off.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-4">
          <label className="flex flex-col gap-1.5">
            <span className="eyebrow">Quiz</span>
            <select
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              className="mono rounded-md border border-mars-line bg-mars-night px-3 py-1.5 text-sm text-mars-white outline-none transition-colors focus:border-mars-orange"
            >
              {quizzes.map((q) => (
                <option key={q.slug} value={q.slug}>
                  {q.meta.title} ({q.slug})
                </option>
              ))}
            </select>
          </label>
          <DateRangePicker
            from={from}
            to={to}
            onChange={(next) => {
              setFrom(next.from);
              setTo(next.to);
            }}
          />
          <button
            type="button"
            disabled={!data || breakdownsLoading}
            onClick={() => {
              if (!data) return;
              const csv = buildBreakdownCsv({
                steps: data.steps,
                breakdowns,
                optionLabels,
              });
              downloadCsv(
                `${quiz.slug}-responses-${from}-to-${to}.csv`,
                csv,
              );
            }}
            className="mono rounded-md border border-mars-line bg-mars-charcoal/40 px-3 py-1.5 text-sm text-mars-white transition-colors hover:border-mars-orange disabled:cursor-not-allowed disabled:opacity-40"
          >
            Download CSV
          </button>
        </div>
      </header>

      <ExperimentStatusBar slug={quiz.slug} experiment={quiz.experiment} />

      <section className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-4">
        <Stat
          label="Visitors"
          hint="Sessions that opened the quiz"
          value={formatNumber(data?.started ?? null)}
        />
        <Stat
          label="Submits"
          hint="Sessions that reached the result page"
          value={formatNumber(data?.completed ?? null)}
        />
        <Stat
          label="Time to Complete"
          hint="Avg. time across submits"
          value={formatDuration(data?.avg_duration_ms ?? null)}
        />
        <Stat
          label="Conversion Rate"
          hint="Submits ÷ Visitors"
          value={
            data?.completion_rate === null || data?.completion_rate === undefined
              ? "—"
              : `${data.completion_rate}%`
          }
          accent
        />
      </section>

      {error ? (
        <div className="rounded-2xl border border-red-900/60 bg-red-950/40 p-6 text-sm text-red-200">
          {error}
        </div>
      ) : (
        <>
          <section className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="lg:col-span-2">
              {loading || !data ? (
                <ChartSkeleton />
              ) : (
                <TimeSeriesChart daily={data.daily} />
              )}
            </div>
            <div className="flex flex-col gap-4">
              <ConversionHero
                rate={data?.completion_rate ?? null}
                daily={data?.daily ?? []}
              />
              <DevicesCard
                devices={
                  data?.devices ?? {
                    desktop: 0,
                    mobile: 0,
                    tablet: 0,
                    unknown: 0,
                  }
                }
              />
            </div>
          </section>

          {data && (
            <div className="mb-5 flex flex-wrap items-center gap-2">
              <span className="eyebrow mr-1">Persona</span>
              <Chip
                active={persona === ""}
                onClick={() => setPersona("")}
                label="All"
              />
              {Object.entries(data.facets.persona_counts).map(([key, n]) => (
                <Chip
                  key={key}
                  active={persona === key}
                  onClick={() => setPersona(key)}
                  label={`${key} (${n})`}
                />
              ))}
            </div>
          )}

          <div className="mb-4 flex flex-wrap items-center gap-x-6 gap-y-3">
            <div className="flex items-center gap-2">
              <span className="eyebrow">Cohort</span>
              <div className="inline-flex gap-1 rounded-xl border border-mars-line bg-mars-charcoal/40 p-1 text-sm">
                {(["all", "completers", "abandoners"] as const).map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setCohort(opt)}
                    className={
                      cohort === opt
                        ? "mono rounded-lg bg-mars-orange px-3 py-1.5 text-[0.65rem] uppercase tracking-[0.14em] text-mars-black"
                        : "mono rounded-lg px-3 py-1.5 text-[0.65rem] uppercase tracking-[0.14em] text-mars-white/70 hover:text-mars-white"
                    }
                  >
                    {opt.charAt(0).toUpperCase() + opt.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="eyebrow">Denominator</span>
              <div className="inline-flex gap-1 rounded-xl border border-mars-line bg-mars-charcoal/40 p-1 text-sm">
                {(["answered", "started"] as const).map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setDenominator(opt)}
                    className={
                      denominator === opt
                        ? "mono rounded-lg bg-mars-orange px-3 py-1.5 text-[0.65rem] uppercase tracking-[0.14em] text-mars-black"
                        : "mono rounded-lg px-3 py-1.5 text-[0.65rem] uppercase tracking-[0.14em] text-mars-white/70 hover:text-mars-white"
                    }
                  >
                    {`% of ${opt}`}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {loading || !data ? (
            <TableSkeleton />
          ) : (
            <PagesTable
              started={data.started}
              completed={data.completed}
              steps={data.steps}
              questionLabels={labels}
              optionLabels={optionLabels}
              questionTypes={questionTypes}
              denominator={denominator}
              breakdowns={breakdowns}
              breakdownsLoading={breakdownsLoading}
              randomizedQuestionIds={randomizedQuestionIds}
            />
          )}
        </>
      )}
    </div>
  );
}

function downloadCsv(filename: string, content: string) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function Stat({
  label,
  value,
  hint,
  accent,
}: {
  label: string;
  value: string | number;
  hint?: string;
  accent?: boolean;
}) {
  return (
    <div
      className={
        accent
          ? "rounded-2xl border border-mars-orange/40 bg-mars-orange/10 px-5 py-4"
          : "rounded-2xl border border-mars-line bg-mars-charcoal/40 px-5 py-4"
      }
    >
      <div className="eyebrow">{label}</div>
      <div
        className={
          accent
            ? "mono mt-2 text-3xl font-bold tabular-nums text-mars-orange"
            : "mono mt-2 text-3xl font-bold tabular-nums text-mars-white"
        }
      >
        {value}
      </div>
      {hint && (
        <div
          className={
            accent
              ? "mt-1.5 text-[0.7rem] text-mars-orange/70"
              : "mt-1.5 text-[0.7rem] text-mars-white/45"
          }
        >
          {hint}
        </div>
      )}
    </div>
  );
}

function Chip({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? "mono rounded-full border border-mars-orange bg-mars-orange/20 px-3 py-1 text-xs text-mars-orange"
          : "mono rounded-full border border-mars-line bg-mars-charcoal/40 px-3 py-1 text-xs text-mars-white/70 hover:border-mars-orange hover:text-mars-white"
      }
    >
      {label}
    </button>
  );
}

function ChartSkeleton() {
  return (
    <div className="h-[360px] animate-pulse rounded-2xl border border-mars-line bg-mars-charcoal/40" />
  );
}

function TableSkeleton() {
  return (
    <div className="h-[280px] animate-pulse rounded-2xl border border-mars-line bg-mars-charcoal/40" />
  );
}
